import type { Feature, FeatureCollection, Polygon, MultiPolygon, Point, Position } from "geojson";
import type { Job } from "../../../../../api/types";
import type { OverlapIssue, DuplicateIssue, UnmatchedIssue, NearbyJobInfo } from "../WizardContainer.types";

// Import turf functions - available via @rogoag/shared-ts transitive dependency
import * as turf from "@turf/turf";
// Import geojson-rbush for spatial indexing (available as transitive dependency)
import geojsonRbush from "geojson-rbush";

// ============================================================================
// Performance Optimizations
// ============================================================================

/**
 * Counter-based issue ID generator for performance
 * Avoids expensive Date.now() and Math.random() calls
 */
let issueIdCounter = 0;
function generateIssueId(): string {
	return `issue-${++issueIdCounter}`;
}

/**
 * Cache for computed boundary centroids (job.id -> centroid)
 * Avoids recomputing centroids for the same boundary multiple times
 */
const boundaryCentroidCache = new WeakMap<Job, Feature<Point> | null>();

/**
 * Gets the cached centroid for a job's boundary, or computes and caches it
 */
function getCachedBoundaryCentroid(job: Job): Feature<Point> | null {
	if (boundaryCentroidCache.has(job)) {
		return boundaryCentroidCache.get(job) ?? null;
	}
	const boundary = getBoundaryFeature(job);
	if (!boundary) {
		boundaryCentroidCache.set(job, null);
		return null;
	}
	try {
		const centroid = turf.centroid(boundary);
		boundaryCentroidCache.set(job, centroid);
		return centroid;
	} catch {
		boundaryCentroidCache.set(job, null);
		return null;
	}
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gets the boundary feature from a job, handling both single polygons and multi-polygons
 */
function getBoundaryFeature(job: Job): Feature<Polygon | MultiPolygon> | null {
	const boundary = job.files?.boundaryGeoJSON ?? job.fileOptions?.boundaryGeoJSON;
	if (!boundary || !boundary.features || boundary.features.length === 0) {
		return null;
	}
	// Return the first feature (usually there's only one boundary per job)
	return boundary.features[0] as Feature<Polygon | MultiPolygon>;
}

/**
 * Checks if a job has a boundary
 */
function hasBoundary(job: Job): boolean {
	return getBoundaryFeature(job) !== null;
}

/**
 * Checks if a job has points or zones (features that need to match to a boundary)
 */
function hasFeatures(job: Job): boolean {
	const points = job.files?.pointsGeoJSON ?? job.fileOptions?.pointsGeoJSON;
	const zones = job.files?.zonesGeoJSON ?? job.fileOptions?.zonesGeoJSON;
	return (points?.features?.length ?? 0) > 0 || (zones?.features?.length ?? 0) > 0;
}

/**
 * Gets all point features from a job (from pointsGeoJSON)
 */
function getPointFeatures(job: Job): Feature<Point>[] {
	const points = job.files?.pointsGeoJSON ?? job.fileOptions?.pointsGeoJSON;
	if (!points?.features) return [];
	return points.features.filter((f): f is Feature<Point> => f.geometry?.type === "Point");
}

/**
 * Gets all zone features from a job (from zonesGeoJSON)
 */
function getZoneFeatures(job: Job): Feature<Polygon | MultiPolygon>[] {
	const zones = job.files?.zonesGeoJSON ?? job.fileOptions?.zonesGeoJSON;
	if (!zones?.features) return [];
	return zones.features.filter(
		(f): f is Feature<Polygon | MultiPolygon> => f.geometry?.type === "Polygon" || f.geometry?.type === "MultiPolygon",
	);
}

/**
 * Rounds coordinates to 6 decimal places for comparison
 * 6 decimal places = ~0.11 meters precision
 * Uses integer math for performance (multiply to int, round, keep as int for key generation)
 */
const COORD_PRECISION = 1000000;

/**
 * Creates a coordinate key for hashing/comparison
 * Uses integer representation to avoid floating point string conversion overhead
 */
function createCoordinateKey(coords: Position): string {
	// Use integer keys for faster comparison (avoid floating point formatting)
	const x = Math.round(coords[0] * COORD_PRECISION);
	const y = Math.round(coords[1] * COORD_PRECISION);
	return `${x},${y}`;
}

/**
 * Gets the centroid of a job's features (points or zones)
 */
function getFeaturesCentroid(job: Job): Feature<Point> | null {
	const points = getPointFeatures(job);
	const zones = getZoneFeatures(job);

	// Use first point if available
	if (points.length > 0) {
		return points[0];
	}

	// Otherwise use centroid of first zone
	if (zones.length > 0) {
		try {
			return turf.centroid(zones[0]);
		} catch {
			return null;
		}
	}

	return null;
}

/**
 * Gets FilePath property from features
 */
function getFilePath(features: Feature[]): string {
	for (const feature of features) {
		if (feature.properties?.FilePath) {
			return feature.properties.FilePath;
		}
	}
	return "";
}

/**
 * Builds a map of jobId to file paths from entries with jobId and filePath
 * Uses Set for O(1) duplicate checking instead of includes() O(n)
 */
function buildJobFilePaths(entries: Array<{ jobId: string; filePath: string }>): Record<string, string[]> {
	const result: Record<string, string[]> = {};
	const seenPaths: Record<string, Set<string>> = {};

	for (const entry of entries) {
		if (!result[entry.jobId]) {
			result[entry.jobId] = [];
			seenPaths[entry.jobId] = new Set();
		}
		if (entry.filePath && !seenPaths[entry.jobId].has(entry.filePath)) {
			seenPaths[entry.jobId].add(entry.filePath);
			result[entry.jobId].push(entry.filePath);
		}
	}
	return result;
}

/**
 * Calculates the bounding box of a feature
 */
function getBbox(feature: Feature<Polygon | MultiPolygon>): turf.BBox {
	return turf.bbox(feature);
}

/**
 * Checks if two bounding boxes intersect (fast pre-filter)
 */
function bboxesIntersect(bbox1: turf.BBox, bbox2: turf.BBox): boolean {
	// bbox format: [minX, minY, maxX, maxY]
	return !(bbox1[2] < bbox2[0] || bbox1[0] > bbox2[2] || bbox1[3] < bbox2[1] || bbox1[1] > bbox2[3]);
}

/**
 * Creates a geometry key for boundary comparison.
 * Uses coordinate hashing to identify identical geometries.
 * Optimized with pre-allocated arrays and integer-based coordinate keys.
 */
function createBoundaryKey(feature: Feature<Polygon | MultiPolygon>): string {
	const geom = feature.geometry;
	if (geom.type === "Polygon") {
		// Use outer ring coordinates with pre-allocated array
		const ring = geom.coordinates[0];
		const parts = new Array<string>(ring.length);
		for (let i = 0; i < ring.length; i++) {
			const c = ring[i];
			parts[i] = `${Math.round(c[0] * COORD_PRECISION)},${Math.round(c[1] * COORD_PRECISION)}`;
		}
		return parts.join("|");
	} else if (geom.type === "MultiPolygon") {
		// Use all outer rings with pre-allocated arrays
		const polyParts = new Array<string>(geom.coordinates.length);
		for (let p = 0; p < geom.coordinates.length; p++) {
			const ring = geom.coordinates[p][0];
			const parts = new Array<string>(ring.length);
			for (let i = 0; i < ring.length; i++) {
				const c = ring[i];
				parts[i] = `${Math.round(c[0] * COORD_PRECISION)},${Math.round(c[1] * COORD_PRECISION)}`;
			}
			polyParts[p] = parts.join("|");
		}
		return polyParts.join("||");
	}
	return "";
}

// ============================================================================
// Main Detection Functions
// ============================================================================

/**
 * Detects jobs with overlapping boundaries.
 * Returns a list of overlap issues, each containing the jobs whose boundaries intersect.
 *
 * Performance: Uses R-tree spatial indexing for large datasets (50+ jobs)
 * and lazy boundary key computation to avoid overhead for non-overlapping cases.
 */
export function detectOverlappingBoundaries(jobs: Job[]): OverlapIssue[] {
	const issues: OverlapIssue[] = [];

	// Filter to jobs with boundaries and prepare data
	type JobBoundaryItem = {
		job: Job;
		boundary: Feature<Polygon | MultiPolygon>;
		bbox: turf.BBox;
		_boundaryKey?: string; // Lazily computed
	};

	const jobsWithBoundaries: JobBoundaryItem[] = [];
	const jobIdToItem = new Map<string, JobBoundaryItem>();

	for (const job of jobs) {
		const boundary = getBoundaryFeature(job);
		if (!boundary) continue;

		const item: JobBoundaryItem = {
			job,
			boundary,
			bbox: getBbox(boundary),
			// boundaryKey computed lazily when needed
		};
		jobsWithBoundaries.push(item);
		jobIdToItem.set(job.id, item);
	}

	// Need at least 2 jobs to have overlaps
	if (jobsWithBoundaries.length < 2) {
		return [];
	}

	// Helper to get or compute boundary key lazily
	const getBoundaryKey = (item: JobBoundaryItem): string => {
		if (item._boundaryKey === undefined) {
			item._boundaryKey = createBoundaryKey(item.boundary);
		}
		return item._boundaryKey;
	};

	// Build R-tree spatial index for fast bbox queries
	// For small datasets (< 50 jobs), the O(n²) approach is faster due to R-tree overhead
	const useRtree = jobsWithBoundaries.length >= 50;

	// Track which pairs we've already checked
	const checkedPairs = new Set<string>();

	if (useRtree) {
		// Build R-tree index
		const tree = geojsonRbush<Polygon | MultiPolygon>();
		const indexedFeatures: Feature<Polygon | MultiPolygon>[] = [];

		for (const item of jobsWithBoundaries) {
			// Add job ID to feature properties for lookup
			const featureWithId = {
				...item.boundary,
				properties: { ...item.boundary.properties, __jobId: item.job.id },
			};
			indexedFeatures.push(featureWithId);
		}
		tree.load(indexedFeatures);

		// For each job, query the R-tree for potential overlaps
		for (const item1 of jobsWithBoundaries) {
			// Query R-tree for features that intersect this bounding box
			const candidates = tree.search(item1.bbox);

			for (const candidateFeature of candidates.features) {
				const item2JobId = candidateFeature.properties?.__jobId as string;
				if (!item2JobId || item2JobId === item1.job.id) continue;

				const item2 = jobIdToItem.get(item2JobId);
				if (!item2) continue;

				// Create pair key to avoid duplicates
				const pairKey =
					item1.job.id < item2.job.id ? `${item1.job.id}-${item2.job.id}` : `${item2.job.id}-${item1.job.id}`;
				if (checkedPairs.has(pairKey)) continue;
				checkedPairs.add(pairKey);

				// Check for exact duplicates (handled by duplicate detection) - computed lazily
				if (getBoundaryKey(item1) === getBoundaryKey(item2)) continue;

				try {
					const intersection = turf.intersect(item1.boundary, item2.boundary);
					if (intersection) {
						const intersectionArea = turf.area(intersection);
						if (intersectionArea > 1) {
							const jobFilePaths: Record<string, string[]> = {};
							const filePath1 = item1.boundary.properties?.FilePath;
							const filePath2 = item2.boundary.properties?.FilePath;
							if (filePath1) jobFilePaths[item1.job.id] = [filePath1];
							if (filePath2) jobFilePaths[item2.job.id] = [filePath2];

							issues.push({
								id: generateIssueId(),
								jobIds: [item1.job.id, item2.job.id],
								jobs: [item1.job, item2.job],
								intersectionArea,
								intersectionGeometry: intersection as Feature<Polygon | MultiPolygon>,
								jobFilePaths,
							});
						}
					}
				} catch {
					// Skip invalid geometries
				}
			}
		}
	} else {
		// Small dataset: use simple O(n²) with bbox pre-filter
		for (let i = 0; i < jobsWithBoundaries.length; i++) {
			for (let j = i + 1; j < jobsWithBoundaries.length; j++) {
				const item1 = jobsWithBoundaries[i];
				const item2 = jobsWithBoundaries[j];

				// Fast pre-filter: skip if bounding boxes don't intersect
				if (!bboxesIntersect(item1.bbox, item2.bbox)) continue;

				// Create pair key to avoid duplicates
				const pairKey = `${item1.job.id}-${item2.job.id}`;
				if (checkedPairs.has(pairKey)) continue;
				checkedPairs.add(pairKey);

				// Check for exact duplicates (handled by duplicate detection) - computed lazily
				if (getBoundaryKey(item1) === getBoundaryKey(item2)) continue;

				try {
					const intersection = turf.intersect(item1.boundary, item2.boundary);
					if (intersection) {
						const intersectionArea = turf.area(intersection);
						if (intersectionArea > 1) {
							const jobFilePaths: Record<string, string[]> = {};
							const filePath1 = item1.boundary.properties?.FilePath;
							const filePath2 = item2.boundary.properties?.FilePath;
							if (filePath1) jobFilePaths[item1.job.id] = [filePath1];
							if (filePath2) jobFilePaths[item2.job.id] = [filePath2];

							issues.push({
								id: generateIssueId(),
								jobIds: [item1.job.id, item2.job.id],
								jobs: [item1.job, item2.job],
								intersectionArea,
								intersectionGeometry: intersection as Feature<Polygon | MultiPolygon>,
								jobFilePaths,
							});
						}
					}
				} catch {
					// Skip invalid geometries
				}
			}
		}
	}

	return issues;
}

/**
 * Detects duplicate features across jobs.
 * A duplicate is when the same point, polygon, or boundary appears in multiple jobs.
 * Only compares geospatial data, not metadata.
 */
export function detectDuplicateFeatures(jobs: Job[]): DuplicateIssue[] {
	const issues: DuplicateIssue[] = [];

	// Maps coordinate key to list of { jobId, feature, filePath }
	const pointMap = new Map<string, Array<{ jobId: string; feature: Feature<Point>; filePath: string }>>();
	const polygonMap = new Map<
		string,
		Array<{ jobId: string; feature: Feature<Polygon | MultiPolygon>; filePath: string }>
	>();
	const boundaryMap = new Map<
		string,
		Array<{ jobId: string; feature: Feature<Polygon | MultiPolygon>; filePath: string }>
	>();

	// Collect all features from all jobs
	for (const job of jobs) {
		// Process boundaries (check for duplicate field boundaries)
		const boundary = getBoundaryFeature(job);
		if (boundary) {
			const key = createBoundaryKey(boundary);
			const existing = boundaryMap.get(key) || [];
			existing.push({
				jobId: job.id,
				feature: boundary,
				filePath: boundary.properties?.FilePath || "",
			});
			boundaryMap.set(key, existing);
		}

		// Process points
		const points = getPointFeatures(job);
		for (const point of points) {
			const key = createCoordinateKey(point.geometry.coordinates);
			const existing = pointMap.get(key) || [];
			existing.push({
				jobId: job.id,
				feature: point,
				filePath: point.properties?.FilePath || "",
			});
			pointMap.set(key, existing);
		}

		// Process zones/polygons
		const zones = getZoneFeatures(job);
		for (const zone of zones) {
			// Create a key from the polygon's coordinates (simplified)
			const coords = zone.geometry.type === "Polygon" ? zone.geometry.coordinates[0] : zone.geometry.coordinates[0][0];
			const key = coords.map((c) => createCoordinateKey(c)).join("|");
			const existing = polygonMap.get(key) || [];
			existing.push({
				jobId: job.id,
				feature: zone,
				filePath: zone.properties?.FilePath || "",
			});
			polygonMap.set(key, existing);
		}
	}

	// Find duplicates in points (features appearing in multiple jobs)
	for (const [, entries] of pointMap) {
		// Get unique job IDs
		const uniqueJobIds = [...new Set(entries.map((e) => e.jobId))];
		if (uniqueJobIds.length > 1) {
			// This coordinate appears in multiple jobs
			const filePaths = [...new Set(entries.map((e) => e.filePath).filter(Boolean))];
			const jobFilePaths = buildJobFilePaths(entries);
			const affectedJobs = jobs.filter((j) => uniqueJobIds.includes(j.id));

			issues.push({
				id: generateIssueId(),
				jobIds: uniqueJobIds,
				jobs: affectedJobs,
				featureType: "point",
				duplicateCount: entries.length,
				filePaths,
				jobFilePaths,
				duplicateFeatures: entries.map((e) => e.feature),
			});
		}
	}

	// Find duplicates in polygons (features appearing in multiple jobs)
	for (const [, entries] of polygonMap) {
		const uniqueJobIds = [...new Set(entries.map((e) => e.jobId))];
		if (uniqueJobIds.length > 1) {
			const filePaths = [...new Set(entries.map((e) => e.filePath).filter(Boolean))];
			const jobFilePaths = buildJobFilePaths(entries);
			const affectedJobs = jobs.filter((j) => uniqueJobIds.includes(j.id));

			issues.push({
				id: generateIssueId(),
				jobIds: uniqueJobIds,
				jobs: affectedJobs,
				featureType: "polygon",
				duplicateCount: entries.length,
				filePaths,
				jobFilePaths,
				duplicateFeatures: entries.map((e) => e.feature),
			});
		}
	}

	// Find duplicates in boundaries (identical field boundaries across jobs)
	for (const [, entries] of boundaryMap) {
		const uniqueJobIds = [...new Set(entries.map((e) => e.jobId))];
		if (uniqueJobIds.length > 1) {
			// Multiple jobs have the exact same boundary - this is a duplicate
			const filePaths = [...new Set(entries.map((e) => e.filePath).filter(Boolean))];
			const jobFilePaths = buildJobFilePaths(entries);
			const affectedJobs = jobs.filter((j) => uniqueJobIds.includes(j.id));

			issues.push({
				id: generateIssueId(),
				jobIds: uniqueJobIds,
				jobs: affectedJobs,
				featureType: "boundary",
				duplicateCount: entries.length,
				filePaths,
				jobFilePaths,
				duplicateFeatures: entries.map((e) => e.feature),
			});
		}
	}

	return issues;
}

/**
 * Finds jobs with boundaries within a given radius (in miles) of a target job's features.
 *
 * Performance: Uses cached centroids to avoid recomputing for the same boundaries.
 * Optionally accepts a pre-computed centroid cache for batch operations.
 */
export function findNearbyJobs(
	targetJob: Job,
	allJobs: Job[],
	radiusMiles: number,
	centroidCache?: Map<string, Feature<Point>>,
): NearbyJobInfo[] {
	const results: NearbyJobInfo[] = [];

	// Get the target job's centroid
	const targetCentroid = getFeaturesCentroid(targetJob);
	if (!targetCentroid) {
		return [];
	}

	// Check each job (excluding the target job)
	for (const job of allJobs) {
		// Skip the target job itself
		if (job.id === targetJob.id) {
			continue;
		}

		try {
			// Get boundary centroid - use cache if provided, otherwise use per-job cache
			let boundaryCentroid: Feature<Point> | null = null;

			if (centroidCache && centroidCache.has(job.id)) {
				boundaryCentroid = centroidCache.get(job.id) ?? null;
			} else {
				boundaryCentroid = getCachedBoundaryCentroid(job);
			}

			if (!boundaryCentroid) {
				continue;
			}

			// Calculate distance in miles
			const distance = turf.distance(targetCentroid, boundaryCentroid, { units: "miles" });

			if (distance <= radiusMiles) {
				results.push({
					job,
					distanceMiles: distance,
				});
			}
		} catch {
			// Skip jobs with invalid geometries
		}
	}

	// Sort by distance ascending
	results.sort((a, b) => a.distanceMiles - b.distanceMiles);

	return results;
}

/**
 * Detects jobs that have features (points/zones) but no boundary.
 *
 * Performance: Pre-computes centroids for all jobs with boundaries once,
 * then reuses them for all findNearbyJobs calls.
 */
export function detectUnmatchedJobs(jobs: Job[]): UnmatchedIssue[] {
	const issues: UnmatchedIssue[] = [];

	// Pre-compute centroids for all jobs with boundaries
	// This avoids recomputing centroids in each findNearbyJobs call
	const centroidCache = new Map<string, Feature<Point>>();
	for (const job of jobs) {
		const centroid = getCachedBoundaryCentroid(job);
		if (centroid) {
			centroidCache.set(job.id, centroid);
		}
	}

	for (const job of jobs) {
		// Check if job has features but no boundary
		if (hasFeatures(job) && !hasBoundary(job)) {
			// Get the unmatched features
			const points = getPointFeatures(job);
			const zones = getZoneFeatures(job);
			const allFeatures = [...points, ...zones];

			// Create a FeatureCollection of unmatched features
			const unmatchedFeatures: FeatureCollection = {
				type: "FeatureCollection",
				features: allFeatures,
			};

			// Get file path from features
			const filePath = getFilePath(allFeatures);

			// Find nearby jobs with boundaries (within 2 miles)
			// Pass the pre-computed centroid cache for performance
			const nearbyJobs = findNearbyJobs(job, jobs, 2, centroidCache);

			issues.push({
				id: generateIssueId(),
				job,
				unmatchedFeatures,
				filePath,
				nearbyJobs,
			});
		}
	}

	return issues;
}
